import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Wallet, ArrowRight, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { retailer, getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';

/**
 * RetailerWalletCard — live "This month so far" widget.
 * Shows gross wholesale MTD − adjustments MTD = net so far, with a link to
 * the full settlement dashboard. Aligns with the weekly digest email numbers
 * (both call the same MTD helpers on the backend).
 */
export default function RetailerWalletCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await retailer.getWalletMtd();
        if (alive) setData(d);
      } catch (err) {
        if (alive) toast.error(getErrorMessage(err, 'Failed to load wallet'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const gross = Number(data?.gross_wholesale || 0);
  const adjustments = Number(data?.adjustments || 0);
  const net = Number(data?.net_so_far || 0);
  const hasActivity = gross > 0 || adjustments > 0;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow border-emerald-200"
      onClick={() => nav('/retailer/settlements')}
      data-testid="wallet-mtd-card"
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Wallet className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-700">This Month So Far</h3>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !hasActivity ? (
          <p className="text-sm text-gray-500" data-testid="wallet-mtd-empty">
            No activity this month yet.
          </p>
        ) : (
          <>
            <div className="text-3xl font-bold text-emerald-700" data-testid="wallet-mtd-net">
              ${net.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">Net wholesale so far</div>
            <div className="flex justify-between mt-3 pt-3 border-t border-gray-100 text-xs">
              <div>
                <div className="text-gray-500">Gross</div>
                <div className="font-medium text-gray-800" data-testid="wallet-mtd-gross">
                  ${gross.toFixed(2)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-gray-500 flex items-center gap-1 justify-end">
                  <TrendingDown className="w-3 h-3" /> Adjustments
                </div>
                <div className="font-medium text-red-600" data-testid="wallet-mtd-adjustments">
                  -${adjustments.toFixed(2)}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
